import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  authorizeStoreRequest,
  checkAuthStatusRequest,
  clearPendingRoute,
  createStoreRequest,
  logout as logoutAction,
  resendOTPRequest,
  resetAuthState,
  sendOTPRequest,
  verifyOTPRequest,
} from "@/store/slices/auth.slice";
import type { AccessRole, AuthStatusTrigger } from "@/types/auth";
import type { CreateNewStoreRequest } from "@/services/authService";

type AuthScreen = "login" | "verify-otp" | "store-selector" | "new-store";

const SCREEN_STATUS_TRIGGER: Partial<Record<AuthScreen, AuthStatusTrigger>> = {
  login: "manual-retry",
  "store-selector": "selector-load",
  "new-store": "new-store-load",
};

export function useAuth(screen?: AuthScreen) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAppSelector((state) => state.auth);

  useEffect(() => {
    const trigger = screen ? SCREEN_STATUS_TRIGGER[screen] : null;
    if (!trigger || auth.statusLoading || auth.statusChecked) {
      return;
    }

    if (
      screen === "login" &&
      (auth.isOTPSent || auth.authenticationId || auth.isVerified)
    ) {
      return;
    }

    dispatch(checkAuthStatusRequest({ trigger }));
  }, [
    auth.authenticationId,
    auth.isOTPSent,
    auth.isVerified,
    auth.statusChecked,
    auth.statusLoading,
    dispatch,
    screen,
  ]);

  useEffect(() => {
    if (!auth.pendingRoute) {
      return;
    }

    if (auth.pendingRoute !== location.pathname) {
      navigate(auth.pendingRoute, { replace: true });
    }

    dispatch(clearPendingRoute());
  }, [dispatch, location.pathname, navigate, auth.pendingRoute]);

  const currentPhoneNumber = auth.phoneNumber || auth.adminAuth?.phone || "";
  const switchStore = (store: AccessRole) =>
    dispatch(authorizeStoreRequest(store));

  return useMemo(
    () => ({
      ...auth,
      currentPhoneNumber,
      stores: auth.verifiedStores,
      currentStore: auth.currentStoreInfo,
      sendOtp: (payload: {
        phoneNumber: string;
        inviteId?: string;
        channel?: string;
      }) => dispatch(sendOTPRequest(payload)),
      verifyOtp: (otp: string) => dispatch(verifyOTPRequest({ otp })),
      resendOtp: () => dispatch(resendOTPRequest()),
      refreshStatus: (trigger: AuthStatusTrigger = "manual-retry") =>
        dispatch(checkAuthStatusRequest({ trigger })),
      createStore: (payload: CreateNewStoreRequest) =>
        dispatch(createStoreRequest(payload)),
      switchStore,
      authorizeStore: switchStore,
      logout: () => dispatch(logoutAction()),
      resetFlow: () => dispatch(resetAuthState()),
    }),
    [auth, currentPhoneNumber, dispatch, switchStore],
  );
}
